import React, { useCallback, useEffect, useState } from "react";
import { salaryEnums } from "../../../Constant/HR/staffSalary";
import { useNavigate, useParams } from "react-router-dom";
import { viewSalaryStructureApi } from "../../../Api/salaryApi";
import { viewStaffApi, viewProfileApi } from "../../../Api/HRStaffApi";
import {
  checkStatusCodeSuccess,
  getItem,
  getLocalItem,
  handleEditClick,
  isUserProfile,
  RupeesSymbol,
} from "../../../Constant/common";
import { usePageLoader } from "../../../_helper/loading/LoadingContext";
import { salaryStructureTitle } from "../../../Constant/title";
import { Details } from "../../../Constant/HR/SalaryDetails";
import { Edit, History, notFound } from "../../../Constant";
import { DollarSign, AlertCircle, Clock, Edit2, FileText, Shield } from "react-feather";
import BackButton from "../../../BaseComponents/BackButton";
import BaseTooltip from "../../../BaseComponents/BaseTooltip";
import "./salaryStructure.css";

const formatINR = (val) => {
  if (val == null || isNaN(val)) return "—";
  return `₹${Number(val).toLocaleString("en-IN")}`;
};

const SalaryStructure = () => {
  const { staffId } = useParams();
  const { setLoading } = usePageLoader();
  const [salaryStructure, setsalaryStructure] = useState(null);
  const [staffData, setStaffData] = useState(null);
  const isProfile = JSON.parse(getItem(isUserProfile) || false);
  const profileId = getLocalItem("id");
  const navigate = useNavigate();
  const finalId = isProfile ? profileId : staffId;

  useEffect(() => {
    document.title = salaryStructureTitle;
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    const staffApiCall = isProfile ? viewProfileApi(finalId || "") : viewStaffApi(finalId);
    Promise.all([viewSalaryStructureApi(finalId).catch(() => null), staffApiCall.catch(() => null)])
      .then(([salaryResp, staffResp]) => {
        if (salaryResp && checkStatusCodeSuccess(salaryResp?.statusCode)) {
          setsalaryStructure(salaryResp?.data);
        }
        if (staffResp) {
          const staffObj = staffResp?.data || staffResp;
          setStaffData(staffObj);
        }
      })
      .finally(() => setLoading(false));
  }, [finalId, isProfile, setLoading]);

  useEffect(() => {
    fetchData();
    handleEditClick();
  }, [fetchData]);

  // Determine if salary structure is payroll-based from staff view API / organization config
  const orgPayroll =
    staffData?.organization?.is_payroll ??
    staffData?.organization?.isPayroll ??
    staffData?.is_payroll ??
    staffData?.isPayroll;

  const isPayroll =
    typeof orgPayroll === "boolean"
      ? orgPayroll
      : Boolean(
          salaryStructure?.isPayroll === true ||
          salaryStructure?.salary_type === "payroll" ||
          salaryStructure?.is_payroll_based === true ||
          (Array.isArray(salaryStructure?.components) && salaryStructure.components.length > 0) ||
          (Array.isArray(salaryStructure?.earnings) && salaryStructure.earnings.length > 0) ||
          (Array.isArray(salaryStructure?.deductions) && salaryStructure.deductions.length > 0) ||
          Number(salaryStructure?.pfEmployee) > 0 ||
          Number(salaryStructure?.esicAmount) > 0 ||
          Number(salaryStructure?.ptAmount) > 0 ||
          Number(salaryStructure?.tdsAmount) > 0,
        );

  const Row = ({ label, calc, monthly, yearly, isDeduction = false }) => (
    <div className="salary-structure-row">
      <span className="salary-structure-row-label">{label}</span>
      <span className="salary-structure-row-calc">{calc}</span>
      <span className={`salary-structure-row-amount ${isDeduction ? "deduction" : ""}`}>
        {monthly}
      </span>
      <span className={`salary-structure-row-amount ${isDeduction ? "deduction" : ""}`}>
        {yearly}
      </span>
    </div>
  );

  return (
    <div className="animate-fade-in salary-structure-container">
      {/* Header */}
      <div className="salary-structure-header">
        <div>
          <div className="salary-structure-title-row">
            <h2 className="salary-structure-title">{Details.SALARYDETAILSLABEL}</h2>

            {/* Structure Type Badge */}
            {salaryStructure &&
              (isPayroll ? (
                <span className="salary-structure-badge payroll">
                  <span className="salary-structure-badge-dot payroll" />
                  Payroll Structure
                </span>
              ) : (
                <span className="salary-structure-badge flat">
                  <span className="salary-structure-badge-dot flat" />
                  Flat Rate Structure
                </span>
              ))}
          </div>
          <p className="salary-structure-subtitle">
            {isPayroll
              ? "Configured with payroll template components & statutory deductions"
              : "Fixed monthly salary components breakdown"}
          </p>
        </div>

        <div className="salary-structure-actions">
          {isProfile ? (
            <BaseTooltip label={History} color="#4f46e5">
              <button
                onClick={() => navigate("/salaryHistory")}
                className="salary-structure-action-btn history"
              >
                <Clock size={15} strokeWidth={2} />
              </button>
            </BaseTooltip>
          ) : (
            <>
              <BaseTooltip label={History} color="#4f46e5">
                <button
                  onClick={() => navigate(`/salaryHistory/${staffId}`)}
                  className="salary-structure-action-btn history"
                >
                  <Clock size={15} strokeWidth={2} />
                </button>
              </BaseTooltip>
              <BaseTooltip label={salaryEnums.REVISE} color="#047857">
                <button
                  onClick={() => navigate(`/reviseSalaryStructure/${staffId}`)}
                  className="salary-structure-action-btn revise"
                >
                  <FileText size={15} strokeWidth={2} />
                </button>
              </BaseTooltip>
              <BaseTooltip label={Edit} color="#b45309">
                <button
                  onClick={() => navigate(`/editSalaryStructure/${staffId}`)}
                  className="salary-structure-action-btn edit"
                >
                  <Edit2 size={15} strokeWidth={2} />
                </button>
              </BaseTooltip>
            </>
          )}
          <BackButton
            onClick={() =>
              isProfile ? navigate("/profile") : navigate(`/staffProfile/${staffId}`)
            }
          />
        </div>
      </div>

      {salaryStructure ? (
        <div className="salary-structure-card">
          {/* Table header */}
          <div className="salary-structure-table-header">
            {[
              salaryEnums.COMPONENTS,
              salaryEnums.CALCULATION,
              salaryEnums.MONTHLY_AMOUNT,
              salaryEnums.YEARLY_AMOUNT,
            ].map((h) => (
              <span
                key={h}
                className={`salary-structure-th ${h === salaryEnums.COMPONENTS ? "left" : ""}`}
              >
                {h}
              </span>
            ))}
          </div>

          {/* Section label - Fixed Earnings */}
          <div className="salary-structure-section-label">
            <div className="salary-structure-section-inner">
              <DollarSign size={14} strokeWidth={2} className="salary-structure-section-icon" />
              <span className="salary-structure-section-text">{salaryEnums.FIXED_EARNINGS}</span>
            </div>
          </div>

          {/* Dynamic or Default components */}
          {Array.isArray(salaryStructure?.components) && salaryStructure.components.length > 0 ? (
            salaryStructure.components
              .filter((c) => {
                if (isPayroll) {
                  const compName = (c.component_name || c.name || c.label || "").toLowerCase();
                  return !compName.includes("special allowance");
                }
                return true;
              })
              .map((c, i) => (
                <Row
                  key={i}
                  label={c.component_name || c.name || c.label || "Component"}
                  calc={c.calculationType || c.calc || salaryEnums.FIXED}
                  monthly={c.monthly ? RupeesSymbol(c.monthly) : formatINR(c.amount)}
                  yearly={c.yearly ? RupeesSymbol(c.yearly) : formatINR(Number(c.amount || 0) * 12)}
                  isDeduction={c.type === "deduction"}
                />
              ))
          ) : Array.isArray(salaryStructure?.earnings) && salaryStructure.earnings.length > 0 ? (
            salaryStructure.earnings
              .filter((c) => {
                if (isPayroll) {
                  const compName = (c.component_name || c.name || c.label || "").toLowerCase();
                  return !compName.includes("special allowance");
                }
                return true;
              })
              .map((c, i) => (
                <Row
                  key={i}
                  label={c.component_name || c.name || c.label || "Component"}
                  calc={c.calculationType || c.calc || salaryEnums.FIXED}
                  monthly={c.monthly ? RupeesSymbol(c.monthly) : formatINR(c.amount)}
                  yearly={c.yearly ? RupeesSymbol(c.yearly) : formatINR(Number(c.amount || 0) * 12)}
                  isDeduction={false}
                />
              ))
          ) : (
            <>
              <Row
                label={salaryEnums.SYSTEM_BASIC}
                calc={salaryEnums.FIXED}
                monthly={
                  salaryStructure?.monthly_salary
                    ? RupeesSymbol(salaryStructure.monthly_salary)
                    : "—"
                }
                yearly={
                  salaryStructure?.yearly_salary ? RupeesSymbol(salaryStructure.yearly_salary) : "—"
                }
              />
              {!isPayroll && (
                <Row
                  label={salaryEnums.SPECIAL_ALLOWANCE}
                  calc={salaryEnums.FIXED}
                  monthly={
                    salaryStructure?.monthly_special_allowance
                      ? RupeesSymbol(salaryStructure.monthly_special_allowance)
                      : "—"
                  }
                  yearly={
                    salaryStructure?.yearly_special_allowance
                      ? RupeesSymbol(salaryStructure.yearly_special_allowance)
                      : "—"
                  }
                />
              )}
            </>
          )}

          {/* Statutory Deductions section if present */}
          {isPayroll &&
            (Number(salaryStructure?.pfEmployee) > 0 ||
              Number(salaryStructure?.esicAmount) > 0 ||
              Number(salaryStructure?.ptAmount) > 0 ||
              Number(salaryStructure?.tdsAmount) > 0) && (
              <>
                <div className="salary-structure-section-label deductions">
                  <div className="salary-structure-section-inner">
                    <Shield
                      size={14}
                      strokeWidth={2}
                      className="salary-structure-section-icon deductions"
                    />
                    <span className="salary-structure-section-text deductions">
                      STATUTORY DEDUCTIONS
                    </span>
                  </div>
                </div>

                {Number(salaryStructure?.pfEmployee) > 0 && (
                  <Row
                    label="PF (Employee)"
                    calc="12% of Basic"
                    monthly={RupeesSymbol(salaryStructure.pfEmployee)}
                    yearly={RupeesSymbol(Number(salaryStructure.pfEmployee) * 12)}
                    isDeduction={true}
                  />
                )}
                {Number(salaryStructure?.esicAmount) > 0 && (
                  <Row
                    label="ESIC"
                    calc="0.75% of Gross"
                    monthly={RupeesSymbol(salaryStructure.esicAmount)}
                    yearly={RupeesSymbol(Number(salaryStructure.esicAmount) * 12)}
                    isDeduction={true}
                  />
                )}
                {Number(salaryStructure?.ptAmount) > 0 && (
                  <Row
                    label="Professional Tax (PT)"
                    calc="State Slab"
                    monthly={RupeesSymbol(salaryStructure.ptAmount)}
                    yearly={RupeesSymbol(Number(salaryStructure.ptAmount) * 12)}
                    isDeduction={true}
                  />
                )}
                {Number(salaryStructure?.tdsAmount) > 0 && (
                  <Row
                    label="TDS"
                    calc="Tax Slab"
                    monthly={RupeesSymbol(salaryStructure.tdsAmount)}
                    yearly={RupeesSymbol(Number(salaryStructure.tdsAmount) * 12)}
                    isDeduction={true}
                  />
                )}
              </>
            )}

          {/* CTC total */}
          <div className="salary-structure-ctc-row">
            <span className="salary-structure-ctc-label">{salaryEnums.CTC}</span>
            <span />
            <span className="salary-structure-ctc-amount">
              {isPayroll
                ? salaryStructure?.monthly_salary
                  ? RupeesSymbol(salaryStructure.monthly_salary)
                  : salaryStructure?.total_monthly_ctc
                    ? RupeesSymbol(salaryStructure.total_monthly_ctc)
                    : "—"
                : salaryStructure?.total_monthly_ctc
                  ? RupeesSymbol(salaryStructure.total_monthly_ctc)
                  : salaryStructure?.monthly_special_allowance
                    ? RupeesSymbol(
                        Number(salaryStructure?.monthly_salary || 0) +
                          Number(salaryStructure?.monthly_special_allowance || 0),
                      )
                    : salaryStructure?.monthly_salary
                      ? RupeesSymbol(salaryStructure.monthly_salary)
                      : "—"}
            </span>
            <span className="salary-structure-ctc-amount">
              {isPayroll
                ? salaryStructure?.yearly_salary
                  ? RupeesSymbol(salaryStructure.yearly_salary)
                  : salaryStructure?.total_yearly_ctc
                    ? RupeesSymbol(salaryStructure.total_yearly_ctc)
                    : "—"
                : salaryStructure?.total_yearly_ctc
                  ? RupeesSymbol(salaryStructure.total_yearly_ctc)
                  : salaryStructure?.yearly_special_allowance
                    ? RupeesSymbol(
                        Number(salaryStructure?.yearly_salary || 0) +
                          Number(salaryStructure?.yearly_special_allowance || 0),
                      )
                    : salaryStructure?.yearly_salary
                      ? RupeesSymbol(salaryStructure.yearly_salary)
                      : "—"}
            </span>
          </div>
        </div>
      ) : (
        <div className="salary-structure-empty">
          <AlertCircle size={40} className="salary-structure-empty-icon" />
          <p className="salary-structure-empty-text">{notFound.dataNotFound}</p>
        </div>
      )}
    </div>
  );
};

export default SalaryStructure;
